import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import { GetQuizAttemptSummary } from '../types';

export const getQuizAttemptSummary = (
  quizId: string
): Promise<GetQuizAttemptSummary.Root> => {
  return axios.get('/assessment/v1/users/reports/get-quiz-attempt-summary', {
    params: {
      quizId,
    },
  });
};

const generateQuizAttempt = (
  attemptNumber: number
): GetQuizAttemptSummary.Attempt => {
  const totalQuestions = faker.number.int({ min: 15, max: 25 });
  const correctQuestions = faker.number.int({ min: 0, max: totalQuestions });
  const accuracy = Math.round((correctQuestions / totalQuestions) * 100);

  return {
    attemptNumber,
    date: faker.date.recent({ days: 30 }).getTime().toString().slice(0, -3),
    marksObtained: faker.number.int({ min: 0, max: 100 }),
    coinsEarned: faker.number.int({ min: 0, max: 15 }),
    accuracy,
    proficiency: faker.number.float({ min: 0, max: 1, fractionDigits: 2 }),
    speedPercentile: faker.number.float({
      min: 0,
      max: 100,
      fractionDigits: 2,
    }),
    timeSpentSec: faker.number.int({ min: 60, max: 600 }),
    totalQuestions,
    correctQuestions,
    testType: 'QUIZ',
    sessionId: faker.string.uuid(),
  };
};

const generateQuizAttemptSummary = (
  quizId: string
): GetQuizAttemptSummary.Root => {
  const numberOfAttempts = faker.number.int({ min: 1, max: 4 });
  const attempts = Array.from({ length: numberOfAttempts }, (_, index) =>
    generateQuizAttempt(index + 1)
  );

  const coinsEarnedHighest = Math.max(...attempts.map(a => a.coinsEarned));

  return {
    status: 'success',
    responseBody: {
      moduleName: null,
      numberOfAttempts,
      coinsEarnedHighest,
      quizReports: [
        {
          assessmentId: null,
          assessmentType: null,
          moduleId: null,
          topicId: null,
          testType: 'PRE-DEFINED-USER-QUIZ',
          roomId: faker.string.uuid(),
          assessmentName: null,
          assessmentImageUrl: null,
          banner: null,
          roomName: faker.helpers.arrayElement([
            'Math Quiz Challenge',
            'Speed Calculation Quiz',
            'Number Sense Quiz',
            'Mental Math Master',
          ]),
          roomType: 'PRE-DEFINED-USER-QUIZ',
          preDefinedRoomId: quizId,
          reAttemptAllowed: faker.datatype.boolean(),
          viewType: faker.helpers.arrayElement(['POST', 'PRE']),
          attempts,
        },
      ],
    },
    errorCode: 0,
    errorMessage: null,
  };
};

export const getQuizAttemptSummaryMockHandler = http.get(
  `${env.API_URL}/assessment/v1/users/reports/get-quiz-attempt-summary`,
  async ({ request }): Promise<HttpResponse<GetQuizAttemptSummary.Root>> => {
    await networkDelay();

    const url = new URL(request.url);
    const quizId = url.searchParams.get('quizId') ?? 'YYTKOF';

    const mockResponse = generateQuizAttemptSummary(quizId);

    return HttpResponse.json(mockResponse);
  }
);
